// Customers list page — /customers
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";

interface Customer {
  id: string;
  phone: string;
  name: string;
  email: string;
  totalSpent: string;
  orderCount: number;
  isActive: boolean;
  customerType?: string;
  createdAt: string;
}

function fmtVND(n: string) {
  return new Intl.NumberFormat("vi-VN").format(parseFloat(n) || 0) + " ₫";
}

export function CustomersListPage() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const limit = 20;

  // Hit the raw envelope so we can read both `data` (rows) and `meta` (pagination).
  // `apiGet` unwraps `data` only, which would lose meta.total needed for paging.
  const { data, isLoading } = useQuery({
    queryKey: ["customers", q, page],
    queryFn: async () => {
      const res = await apiClient.get<{ data: Customer[]; meta: { total: number } }>(
        `/v1/customers?q=${encodeURIComponent(q)}&limit=${limit}&offset=${page * limit}`,
      );
      return res.data;
    },
  });

  const customers = data?.data ?? [];
  const total = data?.meta?.total ?? 0;
  const pages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Khách hàng</h1>
        <Link
          to="/customers/new"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          + Thêm khách hàng
        </Link>
      </div>

      <input
        type="text"
        placeholder="Tìm theo tên, SĐT..."
        className="w-full max-w-sm rounded-md border bg-background px-3 py-2 text-sm"
        value={q}
        onChange={(e) => { setQ(e.target.value); setPage(0); }}
      />

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              {["SĐT", "Tên", "Email", "Tổng chi", "Đơn hàng", "Trạng thái", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Đang tải...</td></tr>
            )}
            {!isLoading && customers.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Không có khách hàng</td></tr>
            )}
            {customers.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-4 py-3 font-mono">{c.phone}</td>
                <td className="px-4 py-3">
                  <span className="font-medium">{c.name || "—"}</span>
                  {c.customerType === "wholesale" && (
                    <span className="ml-2 rounded px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700">Sỉ</span>
                  )}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.email || "—"}</td>
                <td className="px-4 py-3">{fmtVND(c.totalSpent)}</td>
                <td className="px-4 py-3 text-center">{c.orderCount}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    c.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {c.isActive ? "Hoạt động" : "Ngừng"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <Link to={`/customers/${c.id}` as any} className="text-primary hover:underline text-xs">
                    Chi tiết
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-2">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40"
          >
            ‹ Trước
          </button>
          <span className="text-sm text-muted-foreground">Trang {page + 1}/{pages}</span>
          <button
            disabled={page >= pages - 1}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-40"
          >
            Sau ›
          </button>
        </div>
      )}
    </div>
  );
}
